/**
 * For Theme configuration details, see https://material-ui.com/customization/theming/
 *
 * This is the global MUI theme. Page-level Figma colours that don't map cleanly
 * onto a palette role (e.g. the burgundy hero `#2B1520`) are applied inline via
 * `sx` rather than added here.
 */

import { createTheme, responsiveFontSizes } from '@mui/material/styles';

import { PaletteMode } from '@mui/material';
import { CommonColors } from '@mui/material/styles/createPalette';

export const colors: CommonColors = {
    lightGreen: '#63b84c',
    green: '#2e871c',
    darkGreen: '#005900',
    lightRed: '#fd824a',
    red: '#c4531e',
    darkRed: '#8d2400',
    // Old Legacy colours used from V1
    lightGrey: '#E4E4E6',
    xlightGrey: '#F4F4F7',
    black: '#000',
    black70: 'rgba(0,0,0,0.7)',
    grey: '#696969',
    neutralDark: '#64737D',
    neutral: '#808F99',
    neutralLight: '#CAD0D4',
    neutralXLight: '#DAE0E4',
    neutralXXLight: '#F6F6F6',
    white: '#FFF',
    blue: '#1368D8',
    // SBCC / BIP brand
    burgundy: '#3D1A2C',
    burgundyDark: '#2B1320',
    burgundyHero: '#2B1520',
    burgundyAccent: 'rgb(93, 31, 52)',
};

const fontStack =
    '"Museo Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif';

const generateTheme = (mode: PaletteMode, useResponsiveFontSizes = true) => {
    const theme = createTheme({
        palette: {
            mode,
            common: colors,
            primary: {
                main: '#3D1A2C',
                light: '#5A2940',
                dark: '#2B1320',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#6B6B6B',
                light: '#8B8B8B',
                dark: '#4B4B4B',
                contrastText: '#ffffff',
            },
            background: {
                default: '#f5f5f5',
                paper: '#ffffff',
            },
            text: {
                primary: '#333333',
                secondary: '#666666',
            },
        },
        typography: {
            fontFamily: fontStack,
            h1: { fontFamily: fontStack, fontWeight: 700 },
            h2: { fontFamily: fontStack, fontWeight: 700 },
            h3: { fontFamily: fontStack, fontWeight: 700 },
            h4: { fontFamily: fontStack, fontWeight: 700 },
            h5: { fontFamily: fontStack, fontWeight: 700 },
            h6: { fontFamily: fontStack, fontWeight: 700 },
            body1: {
                fontFamily: fontStack,
                fontWeight: 500,
                letterSpacing: 'normal',
            },
            body2: {
                fontFamily: fontStack,
                fontWeight: 500,
                letterSpacing: 'normal',
            },
            button: {
                fontFamily: fontStack,
                fontWeight: 700,
                textTransform: 'none',
            },
            overline: { fontFamily: fontStack, fontWeight: 700 },
            subtitle1: { fontFamily: fontStack, fontWeight: 700 },
            subtitle2: { fontFamily: fontStack, fontWeight: 700 },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 4,
                        textTransform: 'none',
                        fontWeight: 700,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { borderRadius: 8 },
                    elevation1: { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
                    elevation2: { boxShadow: '0 4px 8px rgba(0,0,0,0.12)' },
                    elevation3: { boxShadow: '0 8px 16px rgba(0,0,0,0.15)' },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    head: { fontWeight: 700 },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 700 },
                },
            },
        },
    });
    return useResponsiveFontSizes ? responsiveFontSizes(theme) : theme;
};

export default generateTheme;
